<template>
  <button
    type="button"
    class="archive-entry-row"
    :data-testid="`archive-entry-${entry.id}`"
    @click="$emit('select', entry)"
  >
    <div class="archive-entry-row__icon" :class="iconClass">
      <i :class="iconName" aria-hidden="true" />
    </div>

    <div class="archive-entry-row__content">
      <div class="archive-entry-row__title-row">
        <span class="archive-entry-row__type-badge">{{ typeLabel }}</span>
        <p class="archive-entry-row__title">{{ entry.title }}</p>
      </div>
      <p class="archive-entry-row__meta">{{ meta }}</p>
    </div>

    <span
      class="archive-entry-row__status-dot"
      :style="{ backgroundColor: statusDotColor }"
      aria-hidden="true"
    />
  </button>
</template>

<script>
import {
  archiveStatusDotColor,
  archiveTypeLabelKey,
  buildArchiveEntryMeta,
} from '@/utils/archiveDisplay';

export default {
  name: 'ArchiveEntryRow',
  props: {
    entry: {
      type: Object,
      required: true,
    },
    userLookup: {
      type: Object,
      default: () => ({}),
    },
    milestoneLookup: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['select'],
  computed: {
    typeLabel() {
      return this.$t(archiveTypeLabelKey(this.entry.entityType));
    },
    meta() {
      return buildArchiveEntryMeta(this.entry, {
        t: this.$t.bind(this),
        userLookup: this.userLookup,
        milestoneLookup: this.milestoneLookup,
      });
    },
    statusDotColor() {
      return archiveStatusDotColor(this.entry);
    },
    iconName() {
      if (this.entry.entityType === 'commitments') return 'pi pi-check';
      if (this.entry.entityType === 'constraints') return 'pi pi-exclamation-triangle';
      if (this.entry.entityType === 'decisions') return 'pi pi-book';
      return 'pi pi-flag';
    },
    iconClass() {
      if (this.entry.entityType === 'commitments' && this.entry.status === 'not_done') {
        return 'archive-entry-row__icon--error';
      }
      return 'archive-entry-row__icon--neutral';
    },
  },
};
</script>

<style scoped>
.archive-entry-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.archive-entry-row:hover {
  background: #fafafa;
}

.archive-entry-row__icon {
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.875rem;
}

.archive-entry-row__icon--neutral {
  background: #f2f2f2;
  color: #6b6b6b;
}

.archive-entry-row__icon--error {
  background: #fef2f2;
  color: #ef4444;
}

.archive-entry-row__content {
  flex: 1;
  min-width: 0;
}

.archive-entry-row__title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.125rem;
}

.archive-entry-row__type-badge {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  background: #f2f2f2;
  color: #6b6b6b;
  letter-spacing: 0.02em;
}

.archive-entry-row__title {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-dark);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.archive-entry-row__meta {
  margin: 0;
  font-size: 12px;
  color: #6b6b6b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.archive-entry-row__status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  flex-shrink: 0;
}
</style>
